import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Music, FileText, Sparkles, Copy, Check, X, MoreVertical, Download, RefreshCw, Trash2, SlidersHorizontal, ArrowDownAZ, ArrowUpAZ, Clock, Calendar, Play, Pause, Maximize2, Film, Image as ImageIcon, User, LogOut, Mic, Upload, Plus, Bell, HelpCircle } from "lucide-react";

import { api } from "./api/client";
import { useAuth } from "./hooks/useAuth";
import Header from "./components/layout/Header";
import SiteFooter from "./components/layout/SiteFooter";
import AccountModal from "./components/modals/AccountModal";
import PricingModal from "./components/modals/PricingModal";
import SubscribeDialog from "./components/modals/SubscribeDialog";
import { PrimaryButton, OutlineButton } from "./components/ui/Buttons";
import Landing from "./pages/Landing";
import PricingPage from "./pages/PricingPage";
import { PrivacyPage, TermsPage, RefundPage } from "./pages/Policies";
import GoogleCallback from "./pages/GoogleCallback";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import GlobalDownsellHost from "./components/GlobalDownsellHost";
import ErrorBoundary from "./components/ErrorBoundary";

import { COMPANY_INFO } from "./constants/company";
import {
  PolicyModal,
  PrivacyContent,
  TermsContent,
  RefundContent,
  HowItWorksContent,
  FAQContent,
  PricingContent,
} from "./components/modals/PolicyModal";
import { TruncatedTitle, ToggleSwitch } from "./components/common/Primitives";
import SongCard from "./components/library/SongCard";
import { LyricsCard, LyricsLibraryCard } from "./components/library/LyricsCards";
import { ClipCard, CoverCard } from "./components/library/ClipCoverCards";
import SongDetailView, { SONG_INSPIRATIONS, LYRICS_INSPIRATIONS } from "./components/song/SongDetailView";
import VideoGenerationWizard from "./components/video/VideoGenerationWizard";
import { VideoCard, VideoDetailView, VideoDebugPanel } from "./components/video/VideoComponents";
import ClipWizard from "./components/clip/ClipWizard";
import ClipDetailView from "./components/clip/ClipDetailView";
import CoverDetailView from "./components/cover/CoverDetailView";


// -------------------- App Shell (/app) --------------------
function AppShell() {
  const { user: localUser, clearAuth } = useAuth();
  const navigate = useNavigate();
  const [authState, setAuthState] = useState(null);
  const [policyModal, setPolicyModal] = useState(null);
  
  // Main navigation state
  const [generateTab, setGenerateTab] = useState('song'); // 'song', 'lyrics', or 'cover'
  const [songMode, setSongMode] = useState('prompt'); // 'prompt' or 'lyrics' (sub-tab for song)
  const [libraryTab, setLibraryTab] = useState('all'); // 'all', 'songs', 'covers', 'lyrics', 'videos'
  const [librarySortBy, setLibrarySortBy] = useState('newest'); // 'newest', 'oldest', 'title'
  const [libraryFilterOpen, setLibraryFilterOpen] = useState(false);
  
  // Detail view state
  const [selectedLyrics, setSelectedLyrics] = useState(null);
  const [selectedSong, setSelectedSong] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedClip, setSelectedClip] = useState(null);
  const [selectedCover, setSelectedCover] = useState(null);
  
  // Music generation state
  const [musicPrompt, setMusicPrompt] = useState("");
  const [musicTitle, setMusicTitle] = useState("");
  const [musicStyle, setMusicStyle] = useState("pop");
  const [musicInstrumental, setMusicInstrumental] = useState(false);
  const [musicVocalGender, setMusicVocalGender] = useState(""); // "", "male", "female", "duet"
  const [musicStyles, setMusicStyles] = useState([]);
  const [musicTasks, setMusicTasks] = useState([]);
  const [musicGenerating, setMusicGenerating] = useState(false);
  const [musicProgress, setMusicProgress] = useState(0);
  
  // Duet lyrics tagging state
  const [showVocalButtons, setShowVocalButtons] = useState(false);
  const [vocalButtonsPosition, setVocalButtonsPosition] = useState({ top: 0, left: 0 });
  const [selectedTextRange, setSelectedTextRange] = useState(null);
  const lyricsTextareaRef = useRef(null);
  
  // Lyrics generation state
  const [lyricsDescription, setLyricsDescription] = useState("");
  const [lyricsTasks, setLyricsTasks] = useState([]);
  const [lyricsGenerating, setLyricsGenerating] = useState(false);
  
  // Cover generation state
  const [voiceModels, setVoiceModels] = useState([]);
  const [selectedVoiceModel, setSelectedVoiceModel] = useState('');
  const [coverMode, setCoverMode] = useState('select'); // 'select' or 'new'
  const [coverAudioUrl, setCoverAudioUrl] = useState('');
  const [coverAudioFile, setCoverAudioFile] = useState(null);
  const [coverGenerating, setCoverGenerating] = useState(false);
  const [covers, setCovers] = useState([]);
  // New voice model creation state
  const [newModelName, setNewModelName] = useState('');
  const [newModelLanguage, setNewModelLanguage] = useState('ENGLISH');
  const [newModelFiles, setNewModelFiles] = useState([]);
  const [newModelTraining, setNewModelTraining] = useState(false);
  
  // Shared state
  const [credits, setCredits] = useState(null);
  const [creditsError, setCreditsError] = useState("");
  const [accountOpen, setAccountOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [offerBannerDismissed, setOfferBannerDismissed] = useState(false);
  const [initialPlan, setInitialPlan] = useState('basic');
  const [showSubscribe, setShowSubscribe] = useState(false);
  
  // Video generation wizard state
  const [videoWizardOpen, setVideoWizardOpen] = useState(false);
  const [videoWizardSong, setVideoWizardSong] = useState(null);
  const [videoWizardStep, setVideoWizardStep] = useState(1); // 1: upload, 2: scene, 3: trim
  const [videoUploadedPhoto, setVideoUploadedPhoto] = useState(null);
  const [videoSelectedScene, setVideoSelectedScene] = useState(null);
  const [videoTrimStart, setVideoTrimStart] = useState(0);
  const [videoTrimEnd, setVideoTrimEnd] = useState(15);
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [videoTasks, setVideoTasks] = useState([]);
  const [debugVideoTask, setDebugVideoTask] = useState(null); // For debug panel
  
  // Clip generation wizard state
  const [clipWizardOpen, setClipWizardOpen] = useState(false);
  const [clipWizardSong, setClipWizardSong] = useState(null);
  const [clipWizardStep, setClipWizardStep] = useState(1); // 1: upload photo, 2: select aspect ratio, 3: view frames
  const [clipUploadedPhoto, setClipUploadedPhoto] = useState(null);
  const [clipAspectRatio, setClipAspectRatio] = useState(null); // '16:9' or '9:16'
  const [clipFrames, setClipFrames] = useState([]);
  const [clipScenario, setClipScenario] = useState('');
  const [clipSelectedFrame, setClipSelectedFrame] = useState(null);
  const [clipGeneratingStory, setClipGeneratingStory] = useState(false);
  const [clipCurrentTask, setClipCurrentTask] = useState(null);
  const [clipTasks, setClipTasks] = useState([]);
  
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const notificationSoundRef = useRef(null);
  const lastFetchedNotificationRef = useRef(null);
  
  // Helper function for time ago
  const getTimeAgo = useCallback((timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  }, []);
  
  // Fetch notifications from backend
  const fetchBackendNotifications = useCallback(async () => {
    if (!localUser?.id) return;
    try {
      const res = await api.get('/notifications');
      const backendNotifications = (res.data || []).map(n => ({
        id: n.id,
        message: n.message,
        type: n.type || 'info',
        read: n.read || false,
        timestamp: new Date(n.created_at),
        timeAgo: getTimeAgo(new Date(n.created_at)),
        fromBackend: true
      }));
      
      // Check for new UNREAD notifications that we haven't seen before
      if (backendNotifications.length > 0) {
        const newUnread = backendNotifications.filter(n => !n.read);
        const latestUnreadId = newUnread.length > 0 ? newUnread[0].id : null;
        
        if (latestUnreadId && lastFetchedNotificationRef.current !== latestUnreadId) {
          // New unread notification arrived - play sound only once
          if (lastFetchedNotificationRef.current !== null) {
            if (notificationSoundRef.current) {
              notificationSoundRef.current.currentTime = 0;
              notificationSoundRef.current.play().catch(() => {});
            }
          }
          lastFetchedNotificationRef.current = latestUnreadId;
        }
      }
      
      // Replace backend notifications entirely (they're the source of truth)
      setNotifications(prev => {
        const localOnly = prev.filter(n => !n.fromBackend);
        return [...localOnly, ...backendNotifications].sort((a, b) => 
          new Date(b.timestamp) - new Date(a.timestamp)
        );
      });
    } catch (e) {
      // Silently fail
    }
  }, [localUser?.id, getTimeAgo]);
  
  // Poll for backend notifications
  useEffect(() => {
    if (!localUser?.id) return;
    fetchBackendNotifications();
    const interval = setInterval(fetchBackendNotifications, 10000); // Poll every 10 seconds
    return () => clearInterval(interval);
  }, [localUser?.id, fetchBackendNotifications]);
  
  // Notification helper functions
  const addNotification = useCallback((message, type = 'info') => {
    const newNotification = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      message,
      type,
      read: false,
      timestamp: new Date(),
      timeAgo: 'Just now'
    };
    setNotifications(prev => [newNotification, ...prev]);
    
    // Play notification sound
    if (notificationSoundRef.current) {
      notificationSoundRef.current.currentTime = 0;
      notificationSoundRef.current.play().catch(() => {});
    }
  }, []);
  
  const markNotificationAsRead = useCallback(async (id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
    // Also mark as read in backend
    try {
      await api.put(`/notifications/${id}/read`);
    } catch (e) {
      // Silently fail
    }
  }, []);
  
  const markAllNotificationsAsRead = useCallback(async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    // Also mark all as read in backend
    try {
      await api.put('/notifications/read-all');
    } catch (e) {
      // Silently fail
    }
  }, []);
  
  const deleteNotification = useCallback(async (id) => {
    // Remove from local state immediately
    setNotifications(prev => prev.filter(n => n.id !== id));
    // Delete from backend
    try {
      await api.delete(`/notifications/${id}`);
    } catch (e) {
      console.error('Failed to delete notification:', e);
    }
  }, []);
  
  // Update notification time ago
  useEffect(() => {
    const interval = setInterval(() => {
      setNotifications(prev => prev.map(n => ({
        ...n,
        timeAgo: getTimeAgo(n.timestamp)
      })));
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [getTimeAgo]);
  
  // Scene options for video generation
  const VIDEO_SCENES = [
    { id: 'pop', name: 'Pop', image: 'https://customer-assets.emergentagent.com/job_video-wizard-80/artifacts/r2gzk04t_pop.png', prompt: 'A hyper-realistic close-up portrait of a pop singer on a bright neon stage with pink, blue, and purple lighting. Clean face, expressive eyes, modern clothing. A microphone near the mouth. Strong reflections and glamorous lighting. The face must take up most of the frame, with perfect clarity and sharpness. 9:16 format. Vibrant, polished pop-music style.' },
    { id: 'rap', name: 'Rap', image: 'https://customer-assets.emergentagent.com/job_video-wizard-80/artifacts/krsf5k8i_rap.png', prompt: 'A hyper-realistic close-up portrait of a rap artist standing in front of a colorful graffiti wall. Strong confident expression, gold chain, black hoodie or sweatshirt, cap, vibrant street lighting (warm lamps + blue neon). The face must be large and centered in the frame, clearly visible with sharp details. A microphone held close to the mouth. 9:16 format. Cinematic lighting, urban hip-hop atmosphere.' },
    { id: 'rock', name: 'Rock', image: 'https://customer-assets.emergentagent.com/job_video-wizard-80/artifacts/xt6u5smd_rock.png', prompt: 'A hyper-realistic close-up portrait of a rock singer on a dark stage with dramatic spotlights and concert atmosphere. Slightly intense or emotional expression, leather jacket or dark clothing. Smoke and backlights behind him. A microphone close to his mouth. The face must occupy most of the frame, sharp and detailed. 9:16 format. Moody, cinematic rock vibe.' },
    { id: 'country', name: 'Country', image: 'https://customer-assets.emergentagent.com/job_video-wizard-80/artifacts/vmaz7di3_country.png', prompt: 'A hyper-realistic close-up portrait of a country singer inside a rustic wooden barn. Warm golden lighting, hay, wooden textures, a cowboy hat, and soft natural atmosphere. Calm or warm expression. A microphone held in front. The face must be large and clearly visible, centered in the frame. 9:16 format. Soft cinematic country look.' },
  ];

  const fetchMe = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      localStorage.setItem("auth_user", JSON.stringify(r.data));
      try { window.dispatchEvent(new Event("auth:changed")); } catch (err) { /* noop */ }
      setAuthState(true);
    } catch (e) {
      setAuthState(false);
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const fetchMusicTasks = useCallback(async () => {
    try {
      const r = await api.get("/music/tasks");
      setMusicTasks(r.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchLyricsTasks = useCallback(async () => {
    try {
      const r = await api.get("/lyrics/tasks");
      const tasks = r.data || [];
      
      // Transform old format (task with results array) to new format (individual tasks)
      const expandedTasks = [];
      for (const task of tasks) {
        if (task.status === 'completed' && task.results && task.results.length > 0) {
          // Create separate task for each result
          task.results.forEach((result, idx) => {
            expandedTasks.push({
              id: `${task.id}-${idx}`,
              parentId: task.id,
              resultIndex: idx,
              status: 'completed',
              description: task.description,
              title: result.title,
              lyrics: result.lyrics,
              created_at: task.created_at
            });
          });
        } else if (task.status === 'pending') {
          // Create two pending tasks
          expandedTasks.push({
            id: `${task.id}-0`,
            parentId: task.id,
            resultIndex: 0,
            status: 'pending',
            description: task.description,
            title: null,
            lyrics: null,
            created_at: task.created_at
          });
          expandedTasks.push({
            id: `${task.id}-1`,
            parentId: task.id,
            resultIndex: 1,
            status: 'pending',
            description: task.description,
            title: null,
            lyrics: null,
            created_at: task.created_at
          });
        } else {
          // Failed or other status - show as single task
          expandedTasks.push({
            id: `${task.id}-0`,
            parentId: task.id,
            resultIndex: 0,
            status: task.status,
            description: task.description,
            title: null,
            lyrics: null,
            created_at: task.created_at
          });
        }
      }
      setLyricsTasks(expandedTasks);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchCredits = useCallback(async () => {
    setCreditsError("");
    try {
      const r = await api.get("/credits");
      setCredits(r.data || null);
    } catch (e) {
      setCredits(null);
      setCreditsError(e?.response?.data?.detail || "Failed to load credits");
    }
  }, []);

  const fetchMusicStyles = useCallback(async () => {
    try {
      const r = await api.get("/music/styles");
      setMusicStyles(r.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  // Fetch video tasks
  const fetchVideoTasks = useCallback(async () => {
    try {
      const r = await api.get("/video/tasks");
      setVideoTasks(r.data || []);
    } catch (e) { /* ignore for now */ }
  }, []);

  const fetchClipTasks = useCallback(async () => {
    try {
      const r = await api.get("/clip/tasks/all");
      setClipTasks(r.data?.clips || []);
    } catch (e) { /* ignore for now */ }
  }, []);

  const fetchVoiceModels = useCallback(async () => {
    try {
      const r = await api.get("/voice-models");
      setVoiceModels(r.data?.voice_models || []);
    } catch (e) { /* ignore for now */ }
  }, []);

  const fetchCovers = useCallback(async () => {
    try {
      const r = await api.get("/covers");
      setCovers(r.data?.covers || []);
    } catch (e) { /* ignore for now */ }
  }, []);

  useEffect(() => {
    fetchMe();
    fetchMusicStyles();
    const params = new URLSearchParams(window.location.search);
    const paid = params.get('paid') === '1';
    if (paid) {
      try { const url = new URL(window.location.href); url.searchParams.delete('paid'); window.history.replaceState({}, '', url.toString()); } catch (_e) { /* ignore */ }
      fetchCredits();
      fetchMusicTasks();
      fetchLyricsTasks();
    }
    let poll = null;
    const shouldPoll = paid || (typeof window !== 'undefined' && localStorage.getItem('awaiting_payment') === '1');
    if (shouldPoll) {
      let count = 0;
      poll = setInterval(async () => {
        count += 1;
        try {
          await fetchMe();
          await fetchCredits();
        } catch (_e) { /* ignore */ }
        if (count >= 10) {
          clearInterval(poll);
          try { localStorage.removeItem('awaiting_payment'); } catch (_e) { /* ignore */ }
        }
      }, 1000);
    }
    return () => { if (poll) clearInterval(poll); };
  }, [fetchMe, fetchCredits, fetchMusicTasks, fetchLyricsTasks, fetchMusicStyles]);

  const { search } = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(search);
    const sub = params.get('subscribe');
    if (sub === 'basic' || sub === 'pro') {
      setInitialPlan(sub);
      setAccountOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete('subscribe');
      window.history.replaceState({}, '', url.toString());
    }
  }, [search]);

  useEffect(() => {
    if (authState !== true) return;
    fetchCredits();
    fetchMusicTasks();
    fetchLyricsTasks();
    fetchVideoTasks();
    fetchClipTasks();
    fetchVoiceModels();
    fetchCovers();
  }, [authState, fetchCredits, fetchMusicTasks, fetchLyricsTasks, fetchVideoTasks, fetchClipTasks, fetchVoiceModels, fetchCovers]);

  // Poll training voice models
  useEffect(() => {
    const trainingModels = voiceModels.filter(m => m.status === 'training');
    if (trainingModels.length === 0) return;
    
    const poll = setInterval(async () => {
      try {
        const r = await api.get("/voice-models");
        const updatedModels = r.data?.voice_models || [];
        
        // Check for status changes
        for (const oldModel of trainingModels) {
          const newModel = updatedModels.find(m => m.id === oldModel.id);
          if (newModel && newModel.status !== 'training') {
            if (newModel.status === 'ready') {
              addNotification(`Voice model "${newModel.name}" is ready to use!`);
            } else if (newModel.status === 'failed') {
              addNotification(`Voice model "${newModel.name}" training failed.`);
            }
          }
        }
        
        setVoiceModels(updatedModels);
      } catch (e) { /* ignore */ }
    }, 30000); // Poll every 30 seconds
    
    return () => clearInterval(poll);
  }, [voiceModels, addNotification]);

  // Music generation progress bar
  useEffect(() => {
    let timer;
    if (musicGenerating) {
      const start = Date.now();
      const duration = 30000;
      setMusicProgress(5);
      timer = setInterval(() => {
        const elapsed = Date.now() - start;
        const pct = Math.min(95, Math.max(5, Math.floor((elapsed / duration) * 100)));
        setMusicProgress(pct);
      }, 200);
    } else {
      setMusicProgress(0);
    }
    return () => { if (timer) clearInterval(timer); };
  }, [musicGenerating]);

  // Poll pending music tasks
  useEffect(() => {
    const pending = musicTasks.filter(t => t.status === 'pending' || t.status === 'processing' || t.status === 'streaming');
    if (pending.length === 0) return;
    const poll = setInterval(async () => {
      for (const task of pending) {
        try {
          const r = await api.get(`/music/status/${task.id}`);
          const newStatus = r.data?.status;
          
          // Update task when status changes or when we get new data
          if (newStatus === 'completed' || newStatus === 'streaming' || newStatus === 'failed') {
            const updatedTask = { 
              ...task, 
              status: newStatus, 
              audio_url: r.data.audio_url || task.audio_url,
              image_url: r.data.image_url || task.image_url,
              clip_id: r.data.clip_id || task.clip_id,
              title: r.data.title || task.title,
              provider: r.data.provider || task.provider,
              lyrics: r.data.lyrics || task.lyrics,
              video_url: r.data.video_url || task.video_url,
              duration: r.data.duration || task.duration
            };
            setMusicTasks(prev => prev.map(t => t.id === task.id ? updatedTask : t));
            
            // Send notification for completed song
            if (newStatus === 'completed' && task.status !== 'completed') {
              const songTitle = r.data.title || task.title || 'your song';
              addNotification(`Song generation for "${songTitle}" completed successfully`, 'success');
            }
            
            // Send notification for failed song
            if (newStatus === 'failed' && task.status !== 'failed') {
              const songTitle = r.data.title || task.title || 'your song';
              addNotification(`An error occurred during song generation for "${songTitle}"`, 'error');
            }
            
            // Background fetch lyrics timeline for completed Sonic songs
            if (newStatus === 'completed' && r.data.clip_id && r.data.provider === 'sonic') {
              fetchLyricsTimelineInBackground(task.id, r.data.clip_id);
            }
          } else if (newStatus && newStatus !== task.status) {
            setMusicTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
          }
        } catch (e) { /* ignore */ }
      }
    }, 3000);
    return () => clearInterval(poll);
  }, [musicTasks, addNotification]);

  // Background fetch lyrics timeline for karaoke (persists to DB)
  const fetchLyricsTimelineInBackground = useCallback(async (taskId, clipId) => {
    try {
      const r = await api.post('/music/lyrics-timeline', { clip_id: clipId, task_id: taskId });
      if (r.data?.alignment && r.data.alignment.length > 0) {
        setMusicTasks(prev => prev.map(t => t.id === taskId ? { ...t, lyrics_timeline: r.data.alignment } : t));
      }
    } catch (e) { /* ignore - karaoke is optional */ }
  }, []);

  // Poll pending lyrics tasks
  useEffect(() => {
    // Get unique parent IDs of pending tasks
    const pendingParentIds = [...new Set(
      lyricsTasks
        .filter(t => t.status === 'pending' && t.parentId)
        .map(t => t.parentId)
    )];
    if (pendingParentIds.length === 0) return;
    
    const poll = setInterval(async () => {
      for (const parentId of pendingParentIds) {
        try {
          const r = await api.get(`/lyrics/status/${parentId}`);
          if (r.data?.status === 'completed' || r.data?.status === 'failed') {
            const results = r.data.results || [];
            setLyricsTasks(prev => prev.map(t => {
              if (t.parentId === parentId) {
                const result = results[t.resultIndex];
                if (result) {
                  return { 
                    ...t, 
                    status: 'completed', 
                    title: result.title,
                    lyrics: result.lyrics
                  };
                } else if (r.data.status === 'failed') {
                  return { ...t, status: 'failed' };
                }
              }
              return t;
            }));
          }
        } catch (e) { /* ignore */ }
      }
    }, 2000);
    return () => clearInterval(poll);
  }, [lyricsTasks]);

  async function onMusicGenerate() {
    if (!musicPrompt.trim()) { 
      window.alert(songMode === 'prompt' ? "Please describe your song" : "Please enter lyrics"); 
      return; 
    }
    if (songMode === 'lyrics' && !musicTitle.trim()) { 
      window.alert("Please add a title"); 
      return; 
    }
    
    // Build the style/tags string with vocal gender
    let finalStyle = musicStyle;
    let finalPrompt = musicPrompt;
    
    if (musicVocalGender === 'male') {
      finalStyle = `${musicStyle}, gender Male`;
    } else if (musicVocalGender === 'female') {
      finalStyle = `${musicStyle}, gender Female`;
    } else if (musicVocalGender === 'duet') {
      // Base duet prompt addition
      let duetStyleAddition = "Duet song with male and female lead vocals, clear alternation between verses, harmonized chorus, modern production. No other lead voices. Make the contrast obvious: male voice lower, female voice higher.";
      
      // Check if lyrics contain [Male] and [Female] tags
      const hasVocalTags = musicPrompt.includes('[Male]') && musicPrompt.includes('[Female]');
      
      if (hasVocalTags) {
        duetStyleAddition += " Song structure: use the keys [Male] and [Female] for the given line should be performed in the corresponding voice.";
      } else {
        duetStyleAddition += " Chorus sung together (both) with tight harmonies; Include call-and-response lines and a final chorus with both voices in harmony; every verse belongs to one singer only; pre-chorus is line-by-line; chorus is both.";
      }
      
      finalStyle = `${musicStyle}, ${duetStyleAddition}`;
    }
    
    try {
      setMusicGenerating(true);
      const r = await api.post("/music/generate", {
        prompt: finalPrompt,
        title: musicTitle || 'Untitled',
        style: finalStyle,
        instrumental: musicInstrumental,
        mode: songMode
      });
      // Response now contains array of ids (2 for Sonic, 1 for Producer)
      const ids = r.data.ids || [r.data.id];
      const newTasks = ids.map((id, idx) => ({
        id,
        status: r.data.status || 'pending',
        provider: r.data.provider,
        title: ids.length > 1 ? `${musicTitle || 'Untitled'} #${idx + 1}` : (musicTitle || 'Untitled'),
        prompt: finalPrompt,
        style: finalStyle,
        mode: songMode,
        audio_url: null,
        image_url: null,
        created_at: new Date().toISOString()
      }));
      setMusicTasks(prev => [...newTasks, ...prev]);
      setMusicProgress(100);
      setTimeout(() => setMusicGenerating(false), 400);
      setMusicPrompt("");
      setMusicTitle("");
      setMusicVocalGender("");
      fetchCredits();
    } catch (e) {
      setMusicGenerating(false);
      setMusicProgress(100);
      const msg = e?.response?.data?.detail || "Generation failed";
      if (String(msg).toLowerCase().includes('no credits')) {
        setShowSubscribe(true);
      } else {
        window.alert(msg);
      }
      fetchCredits();
    }
  }

  async function onLyricsGenerate() {
    if (!lyricsDescription.trim()) { 
      window.alert("Please describe the song you want lyrics for"); 
      return; 
    }
    
    // Generate temporary IDs for immediate display
    const tempId = `temp-${Date.now()}`;
    const now = new Date().toISOString();
    const description = lyricsDescription;
    
    // Create TWO placeholder tasks IMMEDIATELY before API call
    const newTasks = [
      {
        id: `${tempId}-0`,
        tempId: tempId,
        resultIndex: 0,
        status: 'pending',
        description: description,
        title: null,
        lyrics: null,
        created_at: now
      },
      {
        id: `${tempId}-1`,
        tempId: tempId,
        resultIndex: 1,
        status: 'pending',
        description: description,
        title: null,
        lyrics: null,
        created_at: now
      }
    ];
    
    // Add to state immediately
    setLyricsTasks(prev => [...newTasks, ...prev]);
    setLyricsGenerating(false);
    setLyricsDescription("");
    
    // Now make the API call in background
    try {
      const r = await api.post("/lyrics/generate", { description: description });
      const realTaskId = r.data.id;
      
      // Update temp tasks with real parent ID
      setLyricsTasks(prev => prev.map(t => {
        if (t.tempId === tempId) {
          return { ...t, parentId: realTaskId, id: `${realTaskId}-${t.resultIndex}` };
        }
        return t;
      }));
      
      // If completed immediately, update with results
      if (r.data.status === 'completed') {
        const statusR = await api.get(`/lyrics/status/${realTaskId}`);
        if (statusR.data?.results) {
          const results = statusR.data.results;
          setLyricsTasks(prev => prev.map(t => {
            if (t.parentId === realTaskId && t.resultIndex === 0 && results[0]) {
              return { ...t, status: 'completed', title: results[0].title, lyrics: results[0].lyrics };
            }
            if (t.parentId === realTaskId && t.resultIndex === 1 && results[1]) {
              return { ...t, status: 'completed', title: results[1].title, lyrics: results[1].lyrics };
            }
            return t;
          }));
        }
      }
    } catch (e) {
      // Remove the temp tasks on error
      setLyricsTasks(prev => prev.filter(t => t.tempId !== tempId));
      const msg = e?.response?.data?.detail || "Lyrics generation failed";
      window.alert(msg);
    }
  }

  // Cover generation functions
  async function handleGenerateCover() {
    if (!selectedVoiceModel) {
      window.alert('Please select a voice model');
      return;
    }
    if (!coverAudioFile) {
      window.alert('Please upload a song file to create a cover');
      return;
    }
    
    // Validate the selected voice model is ready
    const voiceModel = voiceModels.find(m => m.id === selectedVoiceModel);
    if (voiceModel && voiceModel.status !== 'ready') {
      window.alert('The selected voice model is still training. Please wait until it is ready.');
      return;
    }
    
    setCoverGenerating(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('voice_model_id', selectedVoiceModel);
      formData.append('file', coverAudioFile.file);
      
      const r = await api.post("/covers/create", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000  // 1 minute timeout
      });
      
      if (r.data?.success) {
        setCovers(prev => [r.data.cover, ...prev]);
        setCoverAudioFile(null);
        fetchCredits();
        addNotification(`Cover "${r.data.cover.title}" is now being generated...`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Cover generation failed";
      window.alert(msg);
    } finally {
      setCoverGenerating(false);
    }
  }

  async function handleCreateVoiceModel() {
    if (!newModelName.trim()) {
      window.alert('Please enter a voice name');
      return;
    }
    if (newModelFiles.length === 0) {
      window.alert('Please upload at least one audio file');
      return;
    }
    
    setNewModelTraining(true);
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', newModelName.trim());
      formData.append('language', newModelLanguage);
      
      // Append all audio files
      for (const fileObj of newModelFiles) {
        formData.append('files', fileObj.file);
      }
      
      const r = await api.post("/voice-models/create", formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (r.data?.success) {
        setVoiceModels(prev => [r.data.voice_model, ...prev]);
        setNewModelName('');
        setNewModelLanguage('ENGLISH');
        setNewModelFiles([]);
        setCoverMode('select');
        // Note: Voice model will be in "training" status - show notification
        addNotification(`Voice model "${newModelName}" is now training. This may take several minutes.`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Voice model creation failed";
      window.alert(msg);
    } finally {
      setNewModelTraining(false);
    }
  }

  // Helper function to get audio duration
  function getAudioDuration(file) {
    return new Promise((resolve) => {
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      audio.src = url;
      audio.addEventListener('loadedmetadata', () => {
        URL.revokeObjectURL(url);
        resolve(audio.duration);
      });
      audio.addEventListener('error', () => {
        URL.revokeObjectURL(url);
        resolve(0);
      });
    });
  }

  // Calculate total duration of new model files
  const newModelTotalDuration = useMemo(() => {
    return newModelFiles.reduce((sum, f) => sum + (f.duration || 0), 0);
  }, [newModelFiles]);

  async function handleCoverAudioFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.mp3')) {
      window.alert('Please upload an MP3 file');
      e.target.value = '';
      return;
    }
    
    // Check duration - max 10 minutes for cover audio
    const duration = await getAudioDuration(file);
    const maxCoverDuration = 10 * 60; // 10 minutes in seconds
    
    if (duration > maxCoverDuration) {
      window.alert(`This audio file is too long (${Math.floor(duration / 60)}:${String(Math.floor(duration % 60)).padStart(2, '0')}). Maximum allowed duration for covers is 10 minutes.`);
      e.target.value = '';
      return;
    }
    
    // Store the actual File object for FormData upload
    setCoverAudioFile({
      name: file.name,
      file: file,  // Store the actual File object
      duration: duration
    });
    e.target.value = '';
  }

  async function handleNewModelFileUpload(e) {
    const files = Array.from(e.target.files || []);
    const maxTotalDuration = 40 * 60; // 40 minutes in seconds
    
    for (const file of files) {
      if (!file.name.toLowerCase().endsWith('.mp3')) {
        window.alert(`${file.name} is not an MP3 file`);
        continue;
      }
      
      // Get audio duration
      const duration = await getAudioDuration(file);
      
      // Check if single file exceeds max
      if (duration > maxTotalDuration) {
        window.alert(`"${file.name}" is too long (${Math.floor(duration / 60)}:${String(Math.floor(duration % 60)).padStart(2, '0')}). Maximum total duration is 40 minutes.`);
        continue;
      }
      
      // Check if adding this file would exceed the limit
      const currentTotal = newModelFiles.reduce((sum, f) => sum + (f.duration || 0), 0);
      if (currentTotal + duration > maxTotalDuration) {
        window.alert(`Cannot add "${file.name}". Adding this file would exceed the 40-minute limit. Current total: ${Math.floor(currentTotal / 60)}:${String(Math.floor(currentTotal % 60)).padStart(2, '0')}`);
        continue;
      }
      
      // Store the actual File object for FormData upload
      setNewModelFiles(prev => [...prev, {
        id: Date.now() + Math.random(),
        name: file.name,
        file: file,  // Store the actual File object
        duration: duration
      }]);
    }
    e.target.value = '';
  }

  function handleRemoveNewModelFile(fileId) {
    setNewModelFiles(prev => prev.filter(f => f.id !== fileId));
  }

  async function handleDeleteCover(coverId) {
    if (!window.confirm('Are you sure you want to delete this cover?')) return;
    
    try {
      await api.delete(`/covers/${coverId}`);
      setCovers(prev => prev.filter(c => c.id !== coverId));
      if (selectedCover?.id === coverId) {
        setSelectedCover(null);
      }
    } catch (e) {
      window.alert('Failed to delete cover');
    }
  }

  function onInspireMe() {
    const idx = Math.floor(Math.random() * SONG_INSPIRATIONS.length);
    const inspiration = SONG_INSPIRATIONS[idx];
    setMusicPrompt(`${inspiration.description}`);
    const styleMatch = musicStyles.find(s => inspiration.style.toLowerCase().includes(s.name.toLowerCase()));
    if (styleMatch) {
      setMusicStyle(styleMatch.name);
    }
  }

  function onLyricsInspireMe() {
    const idx = Math.floor(Math.random() * LYRICS_INSPIRATIONS.length);
    setLyricsDescription(LYRICS_INSPIRATIONS[idx]);
  }

  function useLyricsForMusic(lyrics, title) {
    setMusicPrompt(lyrics);
    setMusicTitle(title);
    setGenerateTab('song');
    setSongMode('lyrics');
    setSelectedLyrics(null);
  }

  function openLyricsDetail(task, resultIndex = 0) {
    // New structure: task already contains title and lyrics directly
    setSelectedLyrics({ ...task, _selectedIndex: resultIndex });
    setSelectedSong(null);
    setSelectedVideo(null);
    setSelectedClip(null);
    setSelectedCover(null);
  }

  function closeLyricsDetail() {
    setSelectedLyrics(null);
  }

  function openSongDetail(task) {
    setSelectedSong(task);
    setSelectedLyrics(null);
    setSelectedVideo(null);
    setSelectedClip(null);
    setSelectedCover(null);
  }

  function closeSongDetail() {
    setSelectedSong(null);
  }

  function openVideoDetail(task) {
    setSelectedVideo(task);
    setSelectedSong(null);
    setSelectedLyrics(null);
    setSelectedClip(null);
    setSelectedCover(null);
  }

  function closeVideoDetail() {
    setSelectedVideo(null);
  }

  function openCoverDetail(cover) {
    setSelectedCover(cover);
    setSelectedSong(null);
    setSelectedLyrics(null);
    setSelectedVideo(null);
    setSelectedClip(null);
  }

  function closeCoverDetail() {
    setSelectedCover(null);
  }

  // Filter and sort library items
  const getLibraryItems = () => {
    let items = [];
    
    // Add songs with type marker (exclude from lyrics, covers, and videos tabs)
    if (libraryTab !== 'lyrics' && libraryTab !== 'videos' && libraryTab !== 'covers') {
      musicTasks.forEach(t => items.push({ ...t, _type: 'song' }));
    }
    
    // Add covers with type marker
    if (libraryTab === 'all' || libraryTab === 'covers') {
      covers.forEach(t => items.push({ ...t, _type: 'cover' }));
    }
    
    // Add lyrics with type marker (exclude from songs, covers, and videos tabs)
    if (libraryTab !== 'songs' && libraryTab !== 'videos' && libraryTab !== 'covers') {
      lyricsTasks.forEach(t => items.push({ ...t, _type: 'lyrics' }));
    }
    
    // Add videos with type marker (in "all" tab - videos tab has its own rendering)
    if (libraryTab === 'all') {
      videoTasks.forEach(t => items.push({ ...t, _type: 'video', title: t.song_title }));
    }
    
    // Add clips with type marker (in "all" tab - videos tab has its own rendering for clips too)
    if (libraryTab === 'all') {
      clipTasks.forEach(t => items.push({ ...t, _type: 'clip', title: t.song_title }));
    }
    
    // Apply sorting to the merged list
    items.sort((a, b) => {
      if (librarySortBy === 'newest') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      } else if (librarySortBy === 'oldest') {
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      } else if (librarySortBy === 'title') {
        const aTitle = a.title || a.song_title || a.description || '';
        const bTitle = b.title || b.song_title || b.description || '';
        return aTitle.localeCompare(bTitle);
      }
      return 0;
    });
    
    return items;
  };

  // Delete song handler
  async function handleDeleteSong(song) {
    if (!window.confirm(`Delete "${song.title || 'this song'}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/music/tasks/${song.id}`);
      setMusicTasks(prev => prev.filter(t => t.id !== song.id));
      setSelectedSong(null);
    } catch (e) {
      window.alert('Failed to delete song');
    }
  }

  // Re-create song handler (populate form with same settings)
  function handleRecreateSong(song) {
    setMusicPrompt(song.prompt || '');
    setMusicTitle(song.title || '');
    setMusicStyle(song.style || 'pop');
    setMusicInstrumental(song.mode === 'prompt' && !song.prompt);
    setSongMode(song.mode || 'lyrics');
    setGenerateTab('song');
    setSelectedSong(null);
  }

  // Video wizard handlers
  function openVideoWizard(song) {
    setVideoWizardSong(song);
    setVideoWizardStep(1);
    setVideoUploadedPhoto(null);
    setVideoSelectedScene(null);
    setVideoTrimStart(0);
    setVideoTrimEnd(15);
    setVideoWizardOpen(true);
    setSelectedSong(null); // Hide song detail view
  }

  function closeVideoWizard() {
    setVideoWizardOpen(false);
    setVideoWizardSong(null);
    setVideoUploadedPhoto(null);
    setVideoSelectedScene(null);
  }

  async function handleVideoGenerate() {
    if (!videoWizardSong || !videoUploadedPhoto || !videoSelectedScene) {
      window.alert('Please complete all steps before generating');
      return;
    }
    
    setVideoGenerating(true);
    try {
      // Convert uploaded photo to base64
      const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      
      const photoBase64 = await toBase64(videoUploadedPhoto);
      
      const response = await api.post('/video/generate', {
        song_id: videoWizardSong.id,
        audio_url: videoWizardSong.audio_url,
        photo_base64: photoBase64,
        scene_id: videoSelectedScene,
        scene_prompt: VIDEO_SCENES.find(s => s.id === videoSelectedScene)?.prompt || '',
        trim_start: videoTrimStart,
        trim_end: videoTrimEnd,
        aspect_ratio: '9:16' // Default to vertical for regular videos
      });
      
      if (response.data?.task_id) {
        // Add to video tasks and start polling
        const newTask = {
          id: response.data.task_id,
          song_id: videoWizardSong.id,
          song_title: videoWizardSong.title,
          status: 'pending',
          created_at: new Date().toISOString()
        };
        setVideoTasks(prev => [newTask, ...prev]);
        closeVideoWizard();
        setLibraryTab('videos');
        // Start polling for this task
        pollVideoTask(response.data.task_id);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Video generation failed';
      window.alert(msg);
    } finally {
      setVideoGenerating(false);
    }
  }

  async function pollVideoTask(taskId) {
    const maxAttempts = 120; // 10 minutes at 5 second intervals
    let attempts = 0;
    let notificationSent = false;
    
    const poll = async () => {
      try {
        const response = await api.get(`/video/status/${taskId}`);
        const task = response.data;
        
        setVideoTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...task } : t));
        
        if (task.status === 'completed' && !notificationSent) {
          notificationSent = true;
          addNotification(`Video generation for "${task.song_title || 'your song'}" completed successfully`, 'success');
          return; // Stop polling
        }
        
        if (task.status === 'failed' && !notificationSent) {
          notificationSent = true;
          const reason = task.error_message ? ` ${String(task.error_message).slice(0, 280)}` : '';
          addNotification(`Video generation failed for "${task.song_title || 'your song'}".${reason}`, 'error');
          return; // Stop polling
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        }
      } catch (e) {
        console.error('Video polling error:', e);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        }
      }
    };
    
    poll();
  }

  // Delete video task
  async function handleDeleteVideo(videoOrId) {
    const videoId = typeof videoOrId === 'string' ? videoOrId : videoOrId?.id;
    if (!videoId) return;
    try {
      await api.delete(`/video/${videoId}`);
      setVideoTasks(prev => prev.filter(t => t.id !== videoId));
      setSelectedVideo(null);
    } catch (e) {
      window.alert('Failed to delete video');
    }
  }

  // Delete a lyrics task
  async function handleDeleteLyrics(lyricsOrId) {
    const lyricsId = typeof lyricsOrId === 'string' ? lyricsOrId : lyricsOrId?.id;
    if (!lyricsId) return;
    try {
      await api.delete(`/lyrics/${lyricsId}`);
      setLyricsTasks(prev => prev.filter(t => t.id !== lyricsId));
      setSelectedLyrics(null);
    } catch (e) {
      window.alert('Failed to delete lyrics');
    }
  }

  // Delete all failed video tasks
  async function handleDeleteAllFailedVideos() {
    if (!window.confirm('Delete all failed videos?')) return;
    try {
      await api.delete('/video/all/failed');
      setVideoTasks(prev => prev.filter(t => t.status !== 'failed'));
    } catch (e) {
      window.alert('Failed to delete failed videos');
    }
  }

  // Delete all failed lyrics tasks
  async function handleDeleteAllFailedLyrics() {
    if (!window.confirm('Delete all failed lyrics?')) return;
    try {
      await api.delete('/lyrics/all/failed');
      setLyricsTasks(prev => prev.filter(t => t.status !== 'failed'));
      if (selectedLyrics?.status === 'failed') setSelectedLyrics(null);
    } catch (e) {
      window.alert('Failed to delete failed lyrics');
    }
  }

  // ==================== CLIP GENERATION HANDLERS ====================
  
  function openClipWizard(song) {
    setClipWizardSong(song);
    setClipWizardStep(1);
    setClipUploadedPhoto(null);
    setClipAspectRatio(null);
    setClipFrames([]);
    setClipScenario('');
    setClipSelectedFrame(null);
    setClipCurrentTask(null);
    setClipWizardOpen(true);
  }

  function closeClipWizard() {
    setClipWizardOpen(false);
    setClipWizardSong(null);
    setClipUploadedPhoto(null);
    setClipAspectRatio(null);
    setClipFrames([]);
    setClipScenario('');
    setClipSelectedFrame(null);
    setClipCurrentTask(null);
  }

  async function handleClipPhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result.split(',')[1];
      setClipUploadedPhoto({ file, base64, preview: ev.target.result });
    };
    reader.readAsDataURL(file);
  }

  async function handleClipGenerateStory() {
    if (!clipWizardSong || !clipUploadedPhoto || !clipAspectRatio) return;
    
    setClipGeneratingStory(true);
    try {
      // Get lyrics from song
      const lyrics = clipWizardSong.lyrics || '';
      const wordTimings = clipWizardSong.lyrics_timeline || [];
      
      // Get song duration from audio or metadata
      const songDuration = clipWizardSong.duration || clipWizardSong.audio_duration || 180; // Default 3 min
      
      // Extract genre from tags/style - get first tag that matches known genres
      const knownGenres = ['pop', 'rock', 'electronic', 'jazz', 'hip-hop', 'ambient', 'classical', 'r&b', 'country', 'metal'];
      const songStyle = (clipWizardSong.style || clipWizardSong.tags || '').toLowerCase();
      let genre = 'pop'; // Default genre
      for (const g of knownGenres) {
        if (songStyle.includes(g)) {
          genre = g;
          break;
        }
      }
      
      const response = await api.post('/clip/generate-story', {
        song_id: clipWizardSong.id,
        lyrics,
        word_timings: wordTimings,
        song_duration: songDuration,
        genre: genre,
        aspect_ratio: clipAspectRatio
      });
      
      if (response.data?.success && response.data?.frames) {
        setClipFrames(response.data.frames);
        setClipScenario(response.data.scenario || '');
        
        // Create clip task
        const createResponse = await api.post('/clip/create', {
          song_id: clipWizardSong.id,
          audio_url: clipWizardSong.audio_url,
          photo_base64: clipUploadedPhoto.base64,
          frames: response.data.frames,
          scenario: response.data.scenario,
          aspect_ratio: clipAspectRatio
        });
        
        if (createResponse.data?.success) {
          setClipCurrentTask(createResponse.data.clip);
          setClipWizardStep(3);
          fetchClipTasks();
        }
      }
    } catch (e) {
      console.error('Story generation error:', e);
      window.alert('Failed to generate story: ' + (e.response?.data?.detail || e.message));
    } finally {
      setClipGeneratingStory(false);
    }
  }

  async function handleClipDrawFrame(frameNumber) {
    if (!clipCurrentTask) return;
    
    try {
      const response = await api.post(`/clip/${clipCurrentTask.id}/draw-frame`, {
        frame_number: frameNumber
      });
      
      // Refresh clip task
      const updated = await api.get(`/clip/${clipCurrentTask.id}`);
      setClipCurrentTask(updated.data);
      setClipFrames(updated.data.frames || []);
      
      return response.data;
    } catch (e) {
      console.error('Draw frame error:', e);
      return { success: false, error: e.message };
    }
  }

  async function handleClipDrawAllFrames() {
    if (!clipCurrentTask) return;
    
    try {
      await api.post(`/clip/${clipCurrentTask.id}/draw-all-frames`);
      
      // Start polling for updates
      pollClipTask(clipCurrentTask.id, clipCurrentTask.name || clipCurrentTask.song_title);
    } catch (e) {
      console.error('Draw all frames error:', e);
      window.alert('Failed to start drawing frames: ' + e.message);
    }
  }

  async function handleClipRegenerateVideo(frameNumber) {
    if (!clipCurrentTask) return;
    
    try {
      await api.post(`/clip/${clipCurrentTask.id}/regenerate-video`, {
        frame_number: frameNumber
      });
      
      // Start polling for updates
      pollClipTask(clipCurrentTask.id, clipCurrentTask.name || clipCurrentTask.song_title);
    } catch (e) {
      console.error('Regenerate video error:', e);
      window.alert('Failed to regenerate video: ' + e.message);
    }
  }

  async function handleClipRegenerateImage(frameNumber) {
    if (!clipCurrentTask) return;
    
    try {
      const response = await api.post(`/clip/${clipCurrentTask.id}/regenerate-image`, {
        frame_number: frameNumber
      });
      
      if (response.data?.success) {
        // Refresh the clip data to show the new image
        const clipResponse = await api.get(`/clip/${clipCurrentTask.id}`);
        const clip = clipResponse.data;
        setClipCurrentTask(clip);
        setClipFrames(clip.frames || []);
        addNotification(`Image for frame ${frameNumber} regenerated successfully!`);
      } else {
        throw new Error(response.data?.error || 'Failed to regenerate image');
      }
    } catch (e) {
      console.error('Regenerate image error:', e);
      window.alert('Failed to regenerate image: ' + (e.response?.data?.detail || e.message));
    }
  }

  async function handleClipGenerateVideos() {
    if (!clipCurrentTask) return;
    
    try {
      await api.post(`/clip/${clipCurrentTask.id}/generate-videos`);
      
      // Start polling for updates
      pollClipTask(clipCurrentTask.id, clipCurrentTask.name || clipCurrentTask.song_title);
    } catch (e) {
      console.error('Generate videos error:', e);
      window.alert('Failed to start video generation: ' + e.message);
    }
  }

  async function pollClipTask(clipId, clipName = 'your clip') {
    let notificationSent = false;
    let lastStatus = null;
    
    const poll = setInterval(async () => {
      try {
        const response = await api.get(`/clip/${clipId}`);
        const clip = response.data;
        
        setClipCurrentTask(clip);
        setClipFrames(clip.frames || []);
        setClipScenario(clip.scenario || '');
        
        const currentClipName = clip.name || clip.song_title || clipName;
        
        // Notify when images complete and videos start
        if (clip.status === 'generating_videos' && lastStatus === 'generating_images' && !notificationSent) {
          addNotification(`Image generation for the clip "${currentClipName}" completed successfully. Starting video generation`, 'info');
        }
        
        lastStatus = clip.status;
        
        // Stop polling if completed or failed
        if (clip.status === 'completed') {
          clearInterval(poll);
          fetchClipTasks();
          if (!notificationSent) {
            notificationSent = true;
            addNotification(`Clip generation for "${currentClipName}" completed successfully`, 'success');
          }
        }
        
        if (clip.status === 'failed') {
          clearInterval(poll);
          fetchClipTasks();
          if (!notificationSent) {
            notificationSent = true;
            addNotification(`An error occurred during clip generation for "${currentClipName}"`, 'error');
          }
        }
      } catch (e) {
        console.error('Poll clip error:', e);
        clearInterval(poll);
      }
    }, 3000);
    
    // Stop polling after 30 minutes
    setTimeout(() => clearInterval(poll), 30 * 60 * 1000);
  }

  async function handleDeleteClip(clipId) {
    try {
      await api.delete(`/clip/${clipId}`);
      setClipTasks(prev => prev.filter(c => c.id !== clipId));
      if (clipCurrentTask?.id === clipId) {
        closeClipWizard();
      }
    } catch (e) {
      window.alert('Failed to delete clip');
    }
  }

  async function openClipForEditing(clip) {
    // Load full clip data
    try {
      const response = await api.get(`/clip/${clip.id}`);
      const fullClip = response.data;
      
      // If clip is completed, show in detail view instead of wizard
      if (fullClip.status === 'completed') {
        setSelectedSong(null);
        setSelectedVideo(null);
        setSelectedClip(fullClip);
        setSelectedLyrics(null);
        setSelectedCover(null);
        return;
      }
      
      // Otherwise, open the wizard for editing
      setClipCurrentTask(fullClip);
      setClipFrames(fullClip.frames || []);
      setClipScenario(fullClip.scenario || '');
      setClipWizardSong({ id: fullClip.song_id, title: fullClip.song_title, audio_url: fullClip.audio_url });
      setClipWizardStep(2);
      setClipWizardOpen(true);
    } catch (e) {
      console.error('Failed to load clip:', e);
    }
  }

  if (authState === null) return (
    <div className="auth-callback-container">
      <div className="auth-callback-spinner"></div>
      <div className="auth-callback-text">Loading...</div>
    </div>
  );
  if (authState === false) return <Navigate to="/" replace />;

  const libraryItems = getLibraryItems();

  return (
    <div className="app-shell v2-app">
      {/* Ambient background glows (v2 vibe) */}
      <div className="v2-app-bg" aria-hidden="true">
        <div className="v2-hero-glow v2-glow-1" />
        <div className="v2-hero-glow v2-glow-2" />
      </div>
      {/* Notification Sound */}
      <audio 
        ref={notificationSoundRef} 
        src="https://customer-assets.emergentagent.com/job_voicecover-app/artifacts/p2i8dym8_short-calm-pleasant-notification-sound.mp3"
        preload="auto"
      />
      
      <Header 
        isAuthed={authState === true} 
        user={localUser} 
        credits={credits?.total_remaining}
        onLogout={() => { clearAuth(); navigate('/', { replace: true }); }} 
        onOpenAccount={()=>setAccountOpen(true)} 
        onOpenPricing={()=>setPricingOpen(true)}
        onSignIn={() => {
          // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
          const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
          if (!clientId) { window.alert("Google sign-in is not configured."); return; }
          const redirectUri = window.location.origin + '/auth/google';
          const params = new URLSearchParams({
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: 'code',
            scope: 'openid email profile',
            access_type: 'online',
            include_granted_scopes: 'true',
            prompt: 'select_account',
          });
          window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
        }}
        onOpenPolicy={setPolicyModal}
        notifications={notifications}
        onMarkAsRead={markNotificationAsRead}
        onDeleteNotification={deleteNotification}
        onMarkAllAsRead={markAllNotificationsAsRead}
      />
      
      {/* Sticky Plan Bar */}
      {/* Special Offer Banner - only for trial-eligible users */}
      {!offerBannerDismissed && localUser && !localUser.subscription_active && !localUser.free_trial_used && (
        <div className="offer-banner">
          <div className="offer-banner-inner">
            <div className="offer-banner-content">
              <span className="offer-banner-text">
                <strong>Special offer for you.</strong> A great trial offer with <strong>all features included</strong>
              </span>
              <button 
                className="offer-banner-btn"
                onClick={() => setPricingOpen(true)}
                data-testid="offer-banner-learn-more"
              >
                Learn more
              </button>
            </div>
            <button 
              className="offer-banner-close"
              onClick={() => setOfferBannerDismissed(true)}
              aria-label="Dismiss banner"
              data-testid="offer-banner-close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-6xl px-6">
        <div className="app-grid">
          {/* === SECTION 1: Generate === */}
          <div className="panel panel-gen">
            <h2 className="panel-title">Generate</h2>
            
            {/* Top-level tabs: Song / Lyrics / Cover */}
            <div className="tab-switcher-main">
              <button 
                className={`tab-main ${generateTab === 'song' ? 'tab-main-active' : ''}`}
                onClick={() => setGenerateTab('song')}
                data-testid="gen-tab-song"
              >
                <Music size={18} className="inline mr-2" />
                Song
              </button>
              <button 
                className={`tab-main ${generateTab === 'lyrics' ? 'tab-main-active' : ''}`}
                onClick={() => setGenerateTab('lyrics')}
                data-testid="gen-tab-lyrics"
              >
                <FileText size={18} className="inline mr-2" />
                Lyrics
              </button>
              {/* TEMP-DISABLED 2026-05-18: upstream cover/voice-training service degraded
                  (aimusicgenerator.xyz returns the source file as the cover). Re-enable
                  this <button> AND the `{generateTab === 'cover' && (...)}` block below
                  AND the Library "Covers" filter button when the vendor restores the
                  /voices/{voice_id}/cover transformation. */}
              {false && (
                <button 
                  className={`tab-main ${generateTab === 'cover' ? 'tab-main-active' : ''}`}
                  onClick={() => setGenerateTab('cover')}
                  data-testid="gen-tab-cover"
                >
                  <Mic size={18} className="inline mr-2" />
                  Cover
                </button>
              )}
            </div>

            {/* Song Generation */}
            {generateTab === 'song' && (
              <div className="tab-content" key="song-tab">
                {/* Radio button group: Prompt / Lyrics */}
                <div className="mode-radio-group">
                  <label className={`mode-radio ${songMode === 'prompt' ? 'mode-radio-active' : ''}`}>
                    <input 
                      type="radio" 
                      name="songMode" 
                      value="prompt" 
                      checked={songMode === 'prompt'} 
                      onChange={() => setSongMode('prompt')}
                      data-testid="song-mode-prompt"
                    />
                    <span className="mode-radio-indicator" />
                    <span className="mode-radio-label">Prompt</span>
                  </label>
                  <label className={`mode-radio ${songMode === 'lyrics' ? 'mode-radio-active' : ''}`}>
                    <input 
                      type="radio" 
                      name="songMode" 
                      value="lyrics" 
                      checked={songMode === 'lyrics'} 
                      onChange={() => setSongMode('lyrics')}
                      data-testid="song-mode-lyrics"
                    />
                    <span className="mode-radio-indicator" />
                    <span className="mode-radio-label">Lyrics</span>
                  </label>
                </div>

                {songMode === 'prompt' ? (
                  <>
                    <div className="field field-spaced">
                      <label>Song Description</label>
                      <textarea 
                        className="textarea" 
                        rows={5} 
                        value={musicPrompt} 
                        onChange={e=>setMusicPrompt(e.target.value)} 
                        placeholder="Describe the song you want to create..."
                        data-testid="music-prompt" 
                      />
                    </div>
                    <div className="inspire-row">
                      <OutlineButton onClick={onInspireMe} data-testid="inspire-me-btn">
                        <Sparkles size={16} className="inline mr-2" />
                        Inspire Me
                      </OutlineButton>
                      <ToggleSwitch
                        checked={musicInstrumental}
                        onChange={setMusicInstrumental}
                        label="Instrumental"
                        disabled={false}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="field field-spaced" style={{ position: 'relative' }}>
                      <label>Your Lyrics</label>
                      <textarea 
                        ref={lyricsTextareaRef}
                        className="textarea" 
                        rows={6} 
                        value={musicPrompt} 
                        onChange={e=>setMusicPrompt(e.target.value)} 
                        onSelect={(e) => {
                          if (musicVocalGender !== 'duet') return;
                          const textarea = e.target;
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          if (start !== end) {
                            // Get position for buttons
                            const rect = textarea.getBoundingClientRect();
                            setVocalButtonsPosition({
                              top: rect.top + window.scrollY - 40,
                              left: rect.left + window.scrollX + 10
                            });
                            setSelectedTextRange({ start, end });
                            setShowVocalButtons(true);
                          } else {
                            setShowVocalButtons(false);
                          }
                        }}
                        onBlur={() => {
                          // Delay hiding to allow button clicks
                          setTimeout(() => setShowVocalButtons(false), 200);
                        }}
                        placeholder="[Verse 1]&#10;Type your lyrics here..."
                        data-testid="music-lyrics" 
                      />
                      {/* Vocal tag buttons - show when duet mode and text selected */}
                      {showVocalButtons && musicVocalGender === 'duet' && (
                        <div className="vocal-tag-buttons" style={{ position: 'absolute', top: '30px', right: '10px', zIndex: 10 }}>
                          <button 
                            type="button"
                            className="vocal-tag-btn"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (selectedTextRange && lyricsTextareaRef.current) {
                                const { start } = selectedTextRange;
                                const newValue = musicPrompt.slice(0, start) + '[Male]\n' + musicPrompt.slice(start);
                                setMusicPrompt(newValue);
                                setShowVocalButtons(false);
                              }
                            }}
                          >Male</button>
                          <button 
                            type="button"
                            className="vocal-tag-btn"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (selectedTextRange && lyricsTextareaRef.current) {
                                const { start } = selectedTextRange;
                                const newValue = musicPrompt.slice(0, start) + '[Female]\n' + musicPrompt.slice(start);
                                setMusicPrompt(newValue);
                                setShowVocalButtons(false);
                              }
                            }}
                          >Female</button>
                          <button 
                            type="button"
                            className="vocal-tag-btn"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (selectedTextRange && lyricsTextareaRef.current) {
                                const { start } = selectedTextRange;
                                const newValue = musicPrompt.slice(0, start) + '[Both]\n' + musicPrompt.slice(start);
                                setMusicPrompt(newValue);
                                setShowVocalButtons(false);
                              }
                            }}
                          >Both</button>
                        </div>
                      )}
                    </div>
                    <div className="grid-2 gap-3">
                      <div className="field field-spaced">
                        <label>Title</label>
                        <input className="input" value={musicTitle} onChange={e=>setMusicTitle(e.target.value)} placeholder="Song title" data-testid="music-title" />
                      </div>
                      <div className="field field-spaced">
                        <label>Style / Tags</label>
                        <select className="input" value={musicStyle} onChange={e=>setMusicStyle(e.target.value)} data-testid="music-style-select">
                          {musicStyles.map(s => (
                            <option key={s.name} value={s.name}>{s.name}</option>
                          ))}
                          {musicStyles.length === 0 && <option value="pop">pop</option>}
                        </select>
                      </div>
                    </div>
                    <div className="field field-spaced">
                      <label>Vocal Gender</label>
                      <select 
                        className="input" 
                        value={musicVocalGender} 
                        onChange={e=>setMusicVocalGender(e.target.value)} 
                        data-testid="vocal-gender-select"
                      >
                        <option value="">Not specified</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="duet">Duet</option>
                      </select>
                      {musicVocalGender === 'duet' && (
                        <div className="text-xs subtle mt-1">
                          💡 Select text in lyrics and click Male/Female/Both to tag vocal parts
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="mt-3">
                  <PrimaryButton className="btn-full-width" disabled={musicGenerating} onClick={onMusicGenerate} data-testid="music-generate-btn">
                    {musicGenerating ? 'Generating…' : 'Generate Song'}
                  </PrimaryButton>
                </div>
              </div>
            )}

            {/* Lyrics Generation */}
            {generateTab === 'lyrics' && (
              <div className="tab-content" key="lyrics-tab">
                <div className="field field-spaced">
                  <label>Describe the lyrics you want</label>
                  <textarea 
                    className="textarea" 
                    rows={5} 
                    value={lyricsDescription} 
                    onChange={e=>setLyricsDescription(e.target.value)} 
                    placeholder="A love song about summer nights, or a motivational anthem about chasing dreams..."
                    data-testid="lyrics-description" 
                  />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <OutlineButton onClick={onLyricsInspireMe} data-testid="lyrics-inspire-me-btn">
                    <Sparkles size={16} className="inline mr-2" />
                    Inspire Me
                  </OutlineButton>
                </div>
                <div className="mt-3">
                  <PrimaryButton className="btn-full-width" disabled={lyricsGenerating} onClick={onLyricsGenerate} data-testid="lyrics-generate-btn">
                    {lyricsGenerating ? 'Generating…' : 'Generate Lyrics'}
                  </PrimaryButton>
                </div>
              </div>
            )}

            {/* Cover Generation + Voice Training UI */}
            {/* TEMP-DISABLED 2026-05-18: see note at the Cover tab button above.
                Wrapping in `false &&` keeps the entire block as live JSX (so a
                future re-enable is just removing `false &&`) without rendering
                it. Also keeps `coverMode`/`voiceModels` state references valid. */}
            {false && generateTab === 'cover' && (
              <div className="tab-content" key="cover-tab">
                {coverMode === 'select' && (
                  <div className="tab-content" key="cover-select">
                    {/* Voice Models List - show all models with status */}
                    {voiceModels.length > 0 && (
                      <div className="voice-models-list">
                        <div className="voice-models-header">
                          <span className="voice-models-title">Your Voice Models</span>
                        </div>
                        {voiceModels.map(m => (
                          <div 
                            key={m.id} 
                            className={`voice-model-item ${m.status === 'ready' ? 'voice-model-ready' : ''} ${selectedVoiceModel === m.id ? 'voice-model-selected' : ''}`}
                            onClick={() => m.status === 'ready' && setSelectedVoiceModel(m.id)}
                            data-testid={`voice-model-${m.id}`}
                          >
                            <div className="voice-model-icon">
                              <Mic size={20} />
                            </div>
                            <div className="voice-model-info">
                              <span className="voice-model-name">{m.name}</span>
                              <span className="voice-model-language">{m.language}</span>
                            </div>
                            <div className="voice-model-status">
                              {m.status === 'training' ? (
                                <span className="status-training">
                                  <RefreshCw size={14} className="spin" />
                                  Training...
                                </span>
                              ) : m.status === 'ready' ? (
                                <span className="status-ready">Ready</span>
                              ) : m.status === 'failed' ? (
                                <span className="status-failed">Failed</span>
                              ) : (
                                <span className="status-unknown">{m.status}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Voice Model Selection Row - only ready models */}
                    <div className="cover-model-row">
                      <div className="field field-flex">
                        <label>Voice Model</label>
                        <select 
                          className="cover-select"
                          value={selectedVoiceModel}
                          onChange={e => setSelectedVoiceModel(e.target.value)}
                          data-testid="cover-voice-select"
                        >
                          <option value="">Select a voice model...</option>
                          {voiceModels.filter(m => m.status === 'ready').map(m => (
                            <option key={m.id} value={m.id}>{m.name} ({m.language})</option>
                          ))}
                        </select>
                      </div>
                      <OutlineButton 
                        onClick={() => setCoverMode('new')}
                        data-testid="cover-new-model-btn"
                      >
                        <Plus size={16} className="inline mr-2" />
                        New Model
                      </OutlineButton>
                    </div>

                    {/* Show audio input only when voice model is selected */}
                    {selectedVoiceModel && (
                      <>
                        <div className="field field-spaced">
                          <label>Paste your song link</label>
                          <input 
                            type="text"
                            className="input"
                            value={coverAudioUrl}
                            onChange={e => setCoverAudioUrl(e.target.value)}
                            placeholder="https://example.com/song.mp3"
                            data-testid="cover-audio-url"
                          />
                        </div>
                        
                        <div className="cover-upload-area mt-4" data-testid="cover-upload-area">
                          <input 
                            type="file" 
                            accept=".mp3,audio/mpeg" 
                            onChange={handleCoverAudioFileUpload}
                            style={{ display: 'none' }}
                            id="cover-audio-upload"
                          />
                          <label htmlFor="cover-audio-upload" className="cover-upload-label">
                            {coverAudioFile ? (
                              <div className="cover-file-info">
                                <Music size={24} />
                                <span>{coverAudioFile.name.length > 37 ? coverAudioFile.name.slice(0, 37) + '...' : coverAudioFile.name}</span>
                                <button 
                                  className="btn-icon btn-small" 
                                  onClick={(e) => { e.preventDefault(); setCoverAudioFile(null); }}
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ) : (
                              <>
                                <Upload size={32} />
                                <span>Upload File</span>
                                <span className="upload-hint">MP3 only</span>
                              </>
                            )}
                          </label>
                        </div>

                        <div className="mt-4">
                          <PrimaryButton 
                            className="btn-full-width" 
                            disabled={coverGenerating || (!coverAudioUrl && !coverAudioFile)} 
                            onClick={handleGenerateCover}
                            data-testid="cover-generate-btn"
                          >
                            {coverGenerating ? 'Generating…' : 'Generate Cover'}
                          </PrimaryButton>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* New Voice Model Creation */}
                {coverMode === 'new' && (
                  <div className="tab-content" key="cover-new">
                    <div className="new-model-header">
                      <h3>Create New Voice Model</h3>
                      <button 
                        className="btn-icon" 
                        onClick={() => setCoverMode('select')}
                      >
                        <X size={20} />
                      </button>
                    </div>

                    {/* Uploaded Files List */}
                    <div className="new-model-files-section">
                      {newModelFiles.length > 0 && (
                        <div className={`new-model-files ${newModelFiles.length > 2 ? 'scrollable' : ''}`}>
                          {newModelFiles.map(file => (
                            <div key={file.id} className="new-model-file">
                              <Music size={16} />
                              <span className="new-model-file-name">
                                {file.name.length > 37 ? file.name.slice(0, 37) + '...' : file.name}
                              </span>
                              <span className="new-model-file-duration">
                                {Math.floor((file.duration || 0) / 60)}:{String(Math.floor((file.duration || 0) % 60)).padStart(2, '0')}
                              </span>
                              <button 
                                className="btn-icon btn-small" 
                                onClick={() => handleRemoveNewModelFile(file.id)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Upload Button */}
                      <div className={newModelFiles.length > 0 ? "mt-3" : ""}>
                        <input 
                          type="file" 
                          accept=".mp3,audio/mpeg" 
                          multiple
                          onChange={handleNewModelFileUpload}
                          style={{ display: 'none' }}
                          id="new-model-upload"
                        />
                        <label htmlFor="new-model-upload" className="btn-outline btn-upload-files">
                          <Upload size={16} className="inline mr-2" />
                          Upload Audio Files (MP3)
                        </label>
                      </div>

                      {/* Compact Duration Progress Bar - below files */}
                      <div className="duration-progress-compact">
                        <div className="duration-progress-bar-small">
                          <div 
                            className={`duration-progress-fill ${
                              newModelTotalDuration < 600 ? 'duration-yellow' : 
                              newModelTotalDuration < 1500 ? 'duration-green' : 
                              'duration-blue'
                            }`}
                            style={{ width: `${Math.min((newModelTotalDuration / 2400) * 100, 100)}%` }}
                          />
                          {/* Marker lines on the bar */}
                          <div className="duration-bar-marker" style={{ left: '25%' }} />
                          <div className="duration-bar-marker" style={{ left: '75%' }} />
                        </div>
                        <div className="duration-labels-row">
                          <span className="duration-marker-good" style={{ left: '25%' }}>good</span>
                          <span className="duration-uploaded">{Math.floor(newModelTotalDuration / 60)}m / {Math.max(0, 40 - Math.floor(newModelTotalDuration / 60))}m left</span>
                          <span className="duration-marker-great" style={{ left: '75%' }}>great</span>
                        </div>
                      </div>
                    </div>

                    {/* Voice Name */}
                    <div className="new-model-form-section">
                      <div className="field field-spaced">
                        <label>Voice Name</label>
                        <input 
                          type="text"
                          className="input"
                          value={newModelName}
                          onChange={e => setNewModelName(e.target.value)}
                          placeholder="Enter voice model name..."
                          data-testid="new-model-name"
                        />
                      </div>
                    </div>

                    {/* Language Selection */}
                    <div className="new-model-form-section">
                      <div className="field field-spaced">
                        <label>Language</label>
                        <select 
                          className="cover-select"
                          value={newModelLanguage}
                          onChange={e => setNewModelLanguage(e.target.value)}
                          data-testid="new-model-language"
                        >
                          <option value="ENGLISH">ENGLISH</option>
                          <option value="SPANISH">SPANISH</option>
                          <option value="CHINESE">CHINESE</option>
                          <option value="ARABIC">ARABIC</option>
                          <option value="PORTUGUESE">PORTUGUESE</option>
                          <option value="INDONESIAN">INDONESIAN</option>
                          <option value="FRENCH">FRENCH</option>
                          <option value="JAPANESE">JAPANESE</option>
                          <option value="RUSSIAN">RUSSIAN</option>
                          <option value="GERMAN">GERMAN</option>
                          <option value="KOREAN">KOREAN</option>
                          <option value="HINDI">HINDI</option>
                          <option value="TURKISH">TURKISH</option>
                          <option value="ITALIAN">ITALIAN</option>
                          <option value="VIETNAMESE">VIETNAMESE</option>
                          <option value="BENGALI">BENGALI</option>
                          <option value="OTHER">OTHER</option>
                        </select>
                        {newModelTotalDuration > 0 && (
                          <div className="training-time-estimate">
                            Expected training time: {Math.floor(newModelTotalDuration / 60) * 3} - {Math.floor(newModelTotalDuration / 60) * 5} minutes
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Start Training Button */}
                    <div className="mt-4">
                      <PrimaryButton 
                        className="btn-full-width" 
                        disabled={newModelTraining || !newModelName.trim() || newModelFiles.length === 0} 
                        onClick={handleCreateVoiceModel}
                        data-testid="new-model-train-btn"
                      >
                        {newModelTraining ? 'Training…' : 'Start Training'}
                      </PrimaryButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* === SECTION 2: Library === */}
          <div className="panel panel-lib">
            <div className="panel-title-row">
              <h2 className="panel-title">Library</h2>
              <div className="library-actions">
                <div className="sort-menu-container">
                  <button 
                    className="btn-icon-text" 
                    onClick={() => setLibraryFilterOpen(!libraryFilterOpen)}
                    data-testid="library-sort-btn"
                  >
                    <SlidersHorizontal size={16} />
                    <span className="hide-mobile">Sort</span>
                  </button>
                  {libraryFilterOpen && (
                    <div className="sort-menu">
                      <button 
                        className={`sort-menu-item ${librarySortBy === 'newest' ? 'sort-active' : ''}`}
                        onClick={() => { setLibrarySortBy('newest'); setLibraryFilterOpen(false); }}
                      >
                        <Clock size={16} />
                        <span>Newest first</span>
                      </button>
                      <button 
                        className={`sort-menu-item ${librarySortBy === 'oldest' ? 'sort-active' : ''}`}
                        onClick={() => { setLibrarySortBy('oldest'); setLibraryFilterOpen(false); }}
                      >
                        <Calendar size={16} />
                        <span>Oldest first</span>
                      </button>
                      <button 
                        className={`sort-menu-item ${librarySortBy === 'title' ? 'sort-active' : ''}`}
                        onClick={() => { setLibrarySortBy('title'); setLibraryFilterOpen(false); }}
                      >
                        <ArrowDownAZ size={16} />
                        <span>By title</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Library Tabs: All / Songs / Covers / Lyrics / Videos */}
            <div className="lib-tabs">
              <button 
                className={`lib-tab ${libraryTab === 'all' ? 'lib-tab-active' : ''}`}
                onClick={() => setLibraryTab('all')}
                data-testid="lib-tab-all"
              >
                All
              </button>
              <button 
                className={`lib-tab ${libraryTab === 'songs' ? 'lib-tab-active' : ''}`}
                onClick={() => setLibraryTab('songs')}
                data-testid="lib-tab-songs"
              >
                Songs
              </button>
              {/* TEMP-DISABLED 2026-05-18: cover service down. Re-enable together
                  with the Generate-section Cover tab button + content. The library
                  data-fetching / filtering for covers is left intact so any
                  pre-existing covers still show up under the "All" tab. */}
              {false && (
                <button 
                  className={`lib-tab ${libraryTab === 'covers' ? 'lib-tab-active' : ''}`}
                  onClick={() => setLibraryTab('covers')}
                  data-testid="lib-tab-covers"
                >
                  Covers
                </button>
              )}
              <button 
                className={`lib-tab ${libraryTab === 'lyrics' ? 'lib-tab-active' : ''}`}
                onClick={() => setLibraryTab('lyrics')}
                data-testid="lib-tab-lyrics"
              >
                Lyrics
              </button>
              <button 
                className={`lib-tab ${libraryTab === 'videos' ? 'lib-tab-active' : ''}`}
                onClick={() => setLibraryTab('videos')}
                data-testid="lib-tab-videos"
              >
                Videos
              </button>
            </div>

            <div className="library-content">
              {libraryTab === 'videos' ? (
                /* Videos Tab */
                <>
                  {videoTasks.some(t => t.status === 'failed') && (
                    <div className="video-actions-bar">
                      <button 
                        className="btn-outline btn-small btn-danger"
                        onClick={handleDeleteAllFailedVideos}
                        data-testid="clear-failed-videos"
                      >
                        <Trash2 size={14} />
                        Clear Failed Videos
                      </button>
                    </div>
                  )}
                  {(videoTasks.length > 0 || clipTasks.length > 0) ? (
                    <div className="library">
                      {[...videoTasks, ...clipTasks.map(c => ({ ...c, _type: 'clip' }))]
                        .sort((a, b) => {
                          if (librarySortBy === 'newest') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
                          if (librarySortBy === 'oldest') return new Date(a.created_at || 0) - new Date(b.created_at || 0);
                          if (librarySortBy === 'title') return (a.song_title || '').localeCompare(b.song_title || '');
                          return 0;
                        })
                        .map(item => item._type === 'clip' ? (
                          <ClipCard 
                            key={`clip-${item.id}`} 
                            clip={item} 
                            onClick={() => openClipForEditing(item)}
                            onDelete={handleDeleteClip}
                          />
                        ) : (
                          <VideoCard 
                            key={`video-${item.id}`} 
                            task={item} 
                            onClick={() => item.status === 'completed' && openVideoDetail(item)}
                          />
                        ))
                      }
                    </div>
                  ) : (
                    <div className="subtle">No videos yet. Generate one from a song!</div>
                  )}
                </>
              ) : (
                <>
                  {libraryTab === 'lyrics' && lyricsTasks.some(t => t.status === 'failed') && (
                    <div className="video-actions-bar">
                      <button
                        className="btn-outline btn-small btn-danger"
                        onClick={handleDeleteAllFailedLyrics}
                        data-testid="clear-failed-lyrics"
                      >
                        <Trash2 size={14} />
                        Clear Failed Lyrics
                      </button>
                    </div>
                  )}
                  {libraryItems.length > 0 ? (
                    <div className="library">
                      {libraryItems.map(item => (
                        item._type === 'song' ? (
                          <SongCard key={`song-${item.id}`} task={item} onClick={() => openSongDetail(item)} inAllTab={libraryTab === 'all'} />
                        ) : item._type === 'cover' ? (
                          <CoverCard key={`cover-${item.id}`} cover={item} onClick={() => openCoverDetail(item)} onDelete={handleDeleteCover} inAllTab={libraryTab === 'all' || libraryTab === 'covers'} />
                        ) : item._type === 'video' ? (
                          <VideoCard key={`video-${item.id}`} task={item} onClick={() => item.status === 'completed' && openVideoDetail(item)} inAllTab={libraryTab === 'all'} />
                        ) : item._type === 'clip' ? (
                          <ClipCard key={`clip-${item.id}`} clip={item} onClick={() => openClipForEditing(item)} onDelete={handleDeleteClip} inAllTab={libraryTab === 'all'} />
                        ) : (
                          <LyricsLibraryCard key={`lyrics-${item.id}`} task={item} onClick={(task, idx) => openLyricsDetail(task, idx)} inAllTab={libraryTab === 'all'} />
                        )
                      ))}
                    </div>
                  ) : (
                    <div className="subtle">Nothing here yet. Start creating!</div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* === SECTION 3: Detail View === */}
          {selectedLyrics && (
            <div className="panel panel-detail panel-hug">
              <div className="panel-title-row">
                <h2 className="panel-title">Lyrics</h2>
                <div className="panel-title-actions">
                  <button 
                    className="btn-icon btn-icon-danger" 
                    onClick={() => {
                      if (window.confirm('Delete these lyrics?')) {
                        handleDeleteLyrics(selectedLyrics);
                      }
                    }} 
                    title="Delete lyrics"
                    data-testid="delete-lyrics-btn"
                  >
                    <Trash2 size={18} />
                  </button>
                  <button className="btn-icon" onClick={closeLyricsDetail} data-testid="close-lyrics-detail-btn">
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              {selectedLyrics.status === 'completed' && selectedLyrics.title && selectedLyrics.lyrics ? (
                <>
                  <div className="lyrics-detail-single">
                    <h3 className="lyrics-detail-title">{selectedLyrics.title}</h3>
                    <div className="lyrics-detail-content-scroll">
                      <div className="lyrics-detail-text">
                        {/* Handle both actual newlines and escaped \n */}
                        {selectedLyrics.lyrics.replace(/\\n/g, '\n').split('\n').map((line, i, arr) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < arr.length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Full-width button at bottom of section, not in scroll */}
                  <div className="lyrics-detail-footer">
                    <PrimaryButton 
                      className="btn-full-width" 
                      onClick={() => useLyricsForMusic(selectedLyrics.lyrics, selectedLyrics.title)} 
                      data-testid="use-in-song-btn"
                    >
                      <Music size={16} className="inline mr-2" />
                      Use In Song
                    </PrimaryButton>
                  </div>
                </>
              ) : selectedLyrics.status === 'pending' ? (
                <div className="subtle flex items-center gap-2">
                  <span className="spinner" />
                  <span>Generating lyrics...</span>
                </div>
              ) : (
                <div className="subtle text-red">Generation failed</div>
              )}
            </div>
          )}

          {selectedSong && !videoWizardOpen && !clipWizardOpen && (
            <SongDetailView 
              song={selectedSong} 
              onClose={closeSongDetail}
              api={api}
              onDelete={handleDeleteSong}
              onRecreate={handleRecreateSong}
              onGenerateVideo={openVideoWizard}
              onGenerateClip={openClipWizard}
            />
          )}

          {selectedVideo && !videoWizardOpen && !clipWizardOpen && (
            <VideoDetailView 
              video={selectedVideo} 
              onClose={closeVideoDetail}
              onDelete={handleDeleteVideo}
            />
          )}

          {selectedClip && !videoWizardOpen && !clipWizardOpen && (
            <ClipDetailView 
              clip={selectedClip} 
              onClose={() => setSelectedClip(null)}
              onDelete={async (clipId) => {
                await handleDeleteClip(clipId);
                setSelectedClip(null);
              }}
              api={api}
            />
          )}

          {selectedCover && !videoWizardOpen && !clipWizardOpen && (
            <CoverDetailView 
              cover={selectedCover} 
              onClose={closeCoverDetail}
              onDelete={async (coverId) => {
                await handleDeleteCover(coverId);
                closeCoverDetail();
              }}
            />
          )}
        </div>
      </main>

      {/* Clip Wizard Modal */}
      <ClipWizard
        isOpen={clipWizardOpen}
        onClose={closeClipWizard}
        song={clipWizardSong}
        step={clipWizardStep}
        setStep={setClipWizardStep}
        uploadedPhoto={clipUploadedPhoto}
        onPhotoUpload={handleClipPhotoUpload}
        aspectRatio={clipAspectRatio}
        onSelectAspectRatio={setClipAspectRatio}
        frames={clipFrames}
        scenario={clipScenario}
        selectedFrame={clipSelectedFrame}
        onSelectFrame={setClipSelectedFrame}
        currentTask={clipCurrentTask}
        generatingStory={clipGeneratingStory}
        onGenerateStory={handleClipGenerateStory}
        onDrawFrame={handleClipDrawFrame}
        onDrawAllFrames={handleClipDrawAllFrames}
        onGenerateVideos={handleClipGenerateVideos}
        onRegenerateVideo={handleClipRegenerateVideo}
        onRegenerateImage={handleClipRegenerateImage}
      />

      {/* Video Generation Wizard Modal */}
      {videoWizardOpen && videoWizardSong && (
        <VideoGenerationWizard
          song={videoWizardSong}
          step={videoWizardStep}
          setStep={setVideoWizardStep}
          photo={videoUploadedPhoto}
          setPhoto={setVideoUploadedPhoto}
          scene={videoSelectedScene}
          setScene={setVideoSelectedScene}
          trimStart={videoTrimStart}
          setTrimStart={setVideoTrimStart}
          trimEnd={videoTrimEnd}
          setTrimEnd={setVideoTrimEnd}
          generating={videoGenerating}
          onGenerate={handleVideoGenerate}
          onClose={closeVideoWizard}
          scenes={VIDEO_SCENES}
        />
      )}

      <SiteFooter onOpenPolicy={setPolicyModal} />
      <AccountModal
        open={accountOpen}
        onClose={()=>setAccountOpen(false)}
        user={localUser}
        token={undefined}
        onAfterUpdate={()=>{ fetchMe(); fetchCredits(); }}
        initialPlan={initialPlan}
      />
      
      <PricingModal
        open={pricingOpen}
        onClose={() => setPricingOpen(false)}
        user={localUser}
        onAfterUpdate={()=>{ fetchMe(); fetchCredits(); }}
      />

      <SubscribeDialog
        open={showSubscribe}
        onClose={()=>setShowSubscribe(false)}
        onOpenAccount={(plan)=>{ setInitialPlan(plan); setPricingOpen(true); }}
      />
      
      {/* Policy Modals */}
      <PolicyModal open={policyModal === 'howitworks'} onClose={() => setPolicyModal(null)} title="How It Works">
        <HowItWorksContent />
      </PolicyModal>
      <PolicyModal open={policyModal === 'pricing'} onClose={() => setPolicyModal(null)} title="Pricing">
        <PricingContent onClose={() => setPolicyModal(null)} onOpenPricing={() => { setPolicyModal(null); setPricingOpen(true); }} />
      </PolicyModal>
      <PolicyModal open={policyModal === 'faq'} onClose={() => setPolicyModal(null)} title="Frequently Asked Questions">
        <FAQContent />
      </PolicyModal>
      <PolicyModal open={policyModal === 'privacy'} onClose={() => setPolicyModal(null)} title="Privacy Policy">
        <PrivacyContent />
      </PolicyModal>
      <PolicyModal open={policyModal === 'terms'} onClose={() => setPolicyModal(null)} title="Terms of Service">
        <TermsContent />
      </PolicyModal>
      <PolicyModal open={policyModal === 'refund'} onClose={() => setPolicyModal(null)} title="Refund Policy">
        <RefundContent />
      </PolicyModal>
    </div>
  );
}

// -------------------- App Wrapper with Routes --------------------
function AppInner() {
  const { isAuthed } = useAuth();
  return (
    <>
      <Routes>
        <Route path="/" element={<Landing isAuthed={isAuthed} />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/refund" element={<RefundPage />} />
        <Route path="/auth/google" element={<GoogleCallback />} />
        <Route path="/auth/google/" element={<GoogleCallback />} />
        <Route path="/checkout/success" element={<CheckoutSuccess isAuthed={isAuthed} />} />
        <Route path="/app" element={<AppShell />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* Discounted-trial downsell — listens for ?stripe_canceled=1 + the
          sessionStorage hint set by PricingModal. Mounted at app root so
          it works regardless of which route the user lands on. */}
      <GlobalDownsellHost />
    </>
  );
}

export default function AppWrapper() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppInner />
      </BrowserRouter>
    </ErrorBoundary>
  );
}
